import React, { useEffect, useState } from "react";
import { HttpRequest } from "../../utils/common";
import SelectReturn from "./SelectReturn";
import EnterProfile from "./EnterProfile";
import { usePurchaseEcReturnUtilities } from "../../hooks/EcWebsettlement/usePurchaseEcReturnUtilities";
import ConfirmSettlement from "./ConfirmSettlement";
import CompleteSettlement from "./CompleteSettlement";
import Canceled from "./Canceled";
import Error from "./Error";
import Layout from "../../components/Common/Layout";
import { camelcaseKeys } from "../../utils/ApiUtils";
import {
  EcReturn,
  ProjectInformation,
  Questionnaire,
  SocialProfile,
  CardInformation,
  DiscountSetting,
} from "../../types/pages/EcWebsettlement/purchase_ec_return";
import { useScrollToTarget } from "../../hooks/EcWebsettlement/useScrollToTarget";
import Loading from "../../components/Common/Loading";
import { AddNumberDelimiter } from "../../utils/TextUtil";

type Props = {
  ecReturn: EcReturn;
  socialProfile: SocialProfile;
  registeredCard: CardInformation | null;
  questionnaire: Questionnaire;
  projectInformation: ProjectInformation;
  postPath: string;
  cancelPath: string;
  reservationPostPath: string;
  teamDetailCommunityPath: string;
  tweetPath: string;
  facebookSharePath: string;
  payJpPublicKey: string;
  paymentToken: string;
  monthly_destination_value: number | null;
  widget_url: string;
  referral_url: string;
  current_email: string;
  commission: number;
  is_draft?: boolean;
  discount_setting: DiscountSetting;
  is_target_for_discount: boolean;
};
const PurchaseEcReturn = ({
  ecReturn,
  socialProfile,
  registeredCard,
  questionnaire,
  projectInformation,
  postPath,
  cancelPath,
  reservationPostPath,
  teamDetailCommunityPath,
  tweetPath,
  facebookSharePath,
  payJpPublicKey,
  paymentToken,
  monthly_destination_value,
  widget_url,
  referral_url,
  commission,
  is_draft,
  current_email,
  discount_setting,
  is_target_for_discount,
}: Props) => {
  enum CHECKOUT_STATES {
    SelectReturn,
    EnterProfile,
    ConfirmShippingAndPaymentInfo,
    Complete,
    Canceled,
    Error,
  }
  const [settlementResultProps, setSettlementResultProps] = useState({
    previousFunding: 0,
    currentFunding: 0,
    remainingFunding: 0,
    finishedGoal: false,
    targetAmount: 0,
  });

  const [currentPageState, setCurrentPageState] = useState<number>(
    CHECKOUT_STATES.SelectReturn
  );

  const { setScrollToTargetId } = useScrollToTarget(currentPageState)

  const [errorMessage, setErrorMessage] = useState<React.ReactNode>("");
  const [hideErrorBackLink, setHideErrorBackLink] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const {
    control,
    setValue,
    getValues,
    watch,
    trigger,
    register,
    handleSubmit,
    formState: { errors },
    numerableAdditionalAmount,
    amountDetailText,
    totalAmount,
    priceByAmount,
    cardInformation,
    targetDiscountAmount,
  } = usePurchaseEcReturnUtilities(
    paymentToken,
    ecReturn,
    commission,
    current_email,
    socialProfile,
    registeredCard,
    questionnaire,
    discount_setting,
    is_target_for_discount
  );

  const moveSelectReturnPage = (id?: string) => {
    if(id) {
      setScrollToTargetId(id)
    }
    setCurrentPageState(CHECKOUT_STATES.SelectReturn)
  }

  const moveEnterProfilePage = (id?: string) => {
    if(id) {
      setScrollToTargetId(id)
    }
    setCurrentPageState(CHECKOUT_STATES.EnterProfile)
  }

  const moveConfirmPage = () => {
    setCurrentPageState(CHECKOUT_STATES.ConfirmShippingAndPaymentInfo)
  }

  const currentPage = () => {
    switch (currentPageState) {
      case CHECKOUT_STATES.SelectReturn:
        return <SelectReturn {...selectReturnProps} />;
      case CHECKOUT_STATES.EnterProfile:
        return <EnterProfile {...enterProfileProps} />
      case CHECKOUT_STATES.ConfirmShippingAndPaymentInfo:
        return <ConfirmSettlement {...confirmSettlementProps} />
      case CHECKOUT_STATES.Complete:
        return <CompleteSettlement {...completeSettlementProps}/>
        case CHECKOUT_STATES.Canceled:
          return <Canceled projectPath={projectInformation.investment_path} />
      case CHECKOUT_STATES.Error:
        return <Error errorViewMsg={errorMessage} projectPath={projectInformation.investment_path} hideBackLink={hideErrorBackLink}/>;
    }
  };

  const onSubmit = (data) => {
    switch (currentPageState) {
      case CHECKOUT_STATES.SelectReturn:
        // オークションは在庫概念が無いため purchase_reservations による排他制御をスキップ
        if (ecReturn.is_auction) {
          setCurrentPageState(CHECKOUT_STATES.EnterProfile);
          break;
        }
        setIsSubmitting(true);
        HttpRequest.post(reservationPostPath, { number: getValues("number"), ec_return_sku_id: getValues("checked_sku_id") })
        .then(() => {
          setCurrentPageState(CHECKOUT_STATES.EnterProfile)
        })
        .catch((e) => {
          if (e.response.data && e.response.data.code == 1) {
            setErrorMessage("リターンの在庫数が足りません。他のお客様が購入中の場合もございますので、購入個数の変更やしばらく時間を空けて再度お試しいただくようお願いいたします。");
          } else {
            // TODO: hundle other errors
            setErrorMessage("リターンの仮押さえ処理中にエラーが発生しました。再度やり直してもエラーが発生する場合は、お問い合わせよりご連絡ください。");
          }
          setCurrentPageState(CHECKOUT_STATES.Error);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
        break;
      case CHECKOUT_STATES.EnterProfile:
        setCurrentPageState(CHECKOUT_STATES.ConfirmShippingAndPaymentInfo)
        break;
      case CHECKOUT_STATES.ConfirmShippingAndPaymentInfo:
        onPurchase(data)
        break;
    }
  }

  const onPurchase = (data) => {
    setIsSubmitting(true);
    { /* TODO: FormValuesの型と合わせる */}
    data.discount_amount = targetDiscountAmount();
    data.coupon_code = getValues('appliedCoupon')?.code || null;
    HttpRequest.post(postPath, data)
      .then((res) => {
        setSettlementResultProps(camelcaseKeys(res.data));
        setCurrentPageState(CHECKOUT_STATES.Complete);
        history.pushState("", "", `${projectInformation.ec_return_path}web_settlements/complete`);
        (window as any).gtag('config', 'UA-77972820-1', {
          'page_path': `${projectInformation.ec_return_path}web_settlements/complete`
        });
      })
      .catch((e) => {
        const errors = e.response.data.errors;
        setHideErrorBackLink(false);
        if (errors?.reservation) {
          setErrorMessage(errors.reservation);
        } else if (errors?.maximum_price) {
          setErrorMessage(errors.maximum_price);
        } else if (errors?.maximum_price_conveni) {
          setErrorMessage(errors.maximum_price_conveni);
        } else if (errors?.ended) {
          setErrorMessage(errors.ended);
        } else if (errors?.conveni_params_invalid) {
          setErrorMessage(errors.conveni_params_invalid);
        } else if (errors?.remaining_number) {
          setErrorMessage(errors.remaining_number);
        } else if (errors?.card_declined) {
          setErrorMessage(errors.card_declined)
        } else if (errors?.incorrect_card_data) {
          setErrorMessage(errors.incorrect_card_data)
        } else if (errors?.invalid_sku_id) {
          setErrorMessage(errors.invalid_sku_id)
        } else if (errors?.invalid_discount_setting) {
          setErrorMessage(errors.invalid_discount_setting)
        } else if (errors?.auction_payment_method) {
          setErrorMessage(errors.auction_payment_method)
        } else if (errors?.auction_ended) {
          setErrorMessage(errors.auction_ended)
        } else if (errors?.auction_minimum_bid || errors?.auction_outbid_during_bid) {
          // 入札時に他の入札者に最高入札額を上回られた場合 (画面表示中 / 入札処理中の競合)
          // errors.as_json(messages: true) は値を配列で返すため、配列/文字列両対応で正規化
          const raw = errors.auction_minimum_bid || errors.auction_outbid_during_bid;
          const msgStr = Array.isArray(raw) ? raw[0] : raw;
          const matched = typeof msgStr === "string" ? msgStr.match(/([\d,]+)円/) : null;
          const highestAmount = matched ? matched[1] : "";
          setHideErrorBackLink(true);
          setErrorMessage(
            <div className="text-center">
              <p className="text-lg font-bold mb-6">最高入札額が更新されました! あと一歩でした!</p>
              <p className="mb-6">タッチの差で、他の方があなたより高い金額で入札しました。</p>
              <p className="mb-6">現在の最高入札額を上回る金額の入札が必要です。</p>
              {highestAmount && (
                <p className="font-bold text-xl mb-8">現在の最高入札額: {AddNumberDelimiter(highestAmount)}円</p>
              )}
              <p className="mt-6">
                <a
                  href={window.location.pathname}
                  className="left-icon icon-stick-arrow-left text-sm font-medium"
                >
                  入札画面に戻る
                </a>
              </p>
            </div>
          )
        } else if (errors?.quick_bid_not_eligible) {
          setErrorMessage(errors.quick_bid_not_eligible)
        } else if (errors?.quick_bid_no_address) {
          setErrorMessage(errors.quick_bid_no_address)
        } else if (errors?.return_answers) {
          setErrorMessage(errors.return_answers)
        } else {
          // TODO: hundle other errors
          if (getValues('payment_method') === 'conveni') {
            setErrorMessage(
              "リターン購入処理中にエラーが発生しました。決済事業者GMOイプシロン <sendonly@epsilon.jp> から、「コンビニ決済 お支払い受付番号のご案内」というメールが送信された場合、コンビニで入金せずもう一度ご支援をやり直してください。再度やり直してもエラーが発生する場合は、お問い合わせよりご連絡ください。"
            );
          } else {
            setErrorMessage("リターン購入処理中にエラーが発生しました。再度やり直してもエラーが発生する場合は、お問い合わせよりご連絡ください。");
          }
        }
        setCurrentPageState(CHECKOUT_STATES.Error);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const onCancel = () => {
    HttpRequest.delete(cancelPath)
      .then(() => {
        setCurrentPageState(CHECKOUT_STATES.Canceled)
      })
      .catch(() => {
        setErrorMessage("処理中にエラーが発生しました。再度やり直してもエラーが発生する場合は、お問い合わせよりご連絡ください。");
        setCurrentPageState(CHECKOUT_STATES.Error);
      });
  }

  // クイック入札: EnterProfile / ConfirmSettlement をスキップして即時決済
  const handleQuickBid = async () => {
    // クイック入札も通常入札と同様に、表示中のリターン設問へ回答してから送信する。
    if (!(await trigger("answers"))) return;

    const data = getValues() as any;
    data.payment_method = "credit_card";
    data.quick_bid = true;
    onPurchase(data);
  };

  const selectReturnProps = {
    ecReturn,
    projectInformation,
    control,
    setValue,
    getValues,
    watch,
    errors,
    totalAmount,
    priceByAmount,
    onCancel,
    onQuickBid: handleQuickBid,
    monthly_destination_value,
    commission,
    is_draft,
    discount_setting,
    is_target_for_discount,
  };

  const enterProfileProps = {
    cardInformation,
    payJpPublicKey,
    questionnaire,
    projectInformation,
    ecReturn,
    control,
    trigger,
    register,
    setValue,
    watch,
    getValues,
    moveConfirmPage: () => moveConfirmPage(),
    moveSelectReturnPage: () => moveSelectReturnPage(),
    totalAmount,
    priceByAmount,
    commission,
    currentEmail: current_email,
    targetDiscountAmount,
  };

  const confirmSettlementProps = {
    ecReturn,
    questionnaire,
    projectInformation,
    moveSelectReturnPage: (elId?: string) => moveSelectReturnPage(elId),
    moveEnterProfilePage: (elId: string) => moveEnterProfilePage(elId),
    totalAmount,
    priceByAmount,
    getValues,
    setValue,
    cardInformation,
    isSubmitting,
    commission,
    targetDiscountAmount,
  }

  const completeSettlementProps = {
    ecReturn,
    settlementResultProps,
    projectInformation,
    widget_url,
    referral_url,
    getValues,
  }

  return (
    <Layout
      breadcrumbs={[
        {
          text: "HOME",
          href: "/",
        },
        {
          text: projectInformation.investment_title,
          href: is_draft ? `${projectInformation.investment_path}${window.location.search}` : projectInformation.investment_path,
        },
        {
          text: "支援のお手続き"
        },
      ]}
    >
      <Loading
        show={isSubmitting}
      />
      <section className="mt-[40px] px-[30px] sm:max-w-main sm:mb-[40px] sm:mt-[60px] sm:mx-auto md:px-0">
        <h2 className="mb-[40px] sm:mb-[60px] text-lg font-bold sm:text-2xl">支援のお手続き</h2>
        <div className="sm:w-[700px] sm:mx-auto">
          {[
            CHECKOUT_STATES.SelectReturn,
            CHECKOUT_STATES.EnterProfile,
            CHECKOUT_STATES.ConfirmShippingAndPaymentInfo,
            CHECKOUT_STATES.Complete
          ].includes(currentPageState) && (
            <div className="mb-[30px] flex">
              <div className="w-[80px] h-[60px] mr-3 sm:w-[120px] sm:h-[90px]">
                <img
                  className="object-cover"
                  src={ecReturn.investment_image_urls[0]}
                  alt={ecReturn.name}
                />
              </div>
              <a className="w-[calc(100%-92px)] sm:w-[calc(100%-132px)] underline link-hover-none" href={is_draft ? `${ecReturn.purchase_path}${window.location.search}` : ecReturn.purchase_path}>{ecReturn.name}</a>
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>{currentPage()}</form>
        </div>
      </section>
    </Layout>
  )
};

export default PurchaseEcReturn;
